/**
 * REALTY-LIFECYCLE-C1 / MEDIA-EDITOR-PROPERTY-SURFACES-A1 — wizard media helpers.
 */

import {
  QUEUE_PHASE,
  canRetryUpload,
  createQueueItem,
  isItemUploadReady,
  itemsByKind,
  normalizeUploadError,
  requireUploadFile,
  resetItemForUploadRetry,
  revokeQueueItemPreview,
} from "../../../shared/propertyMediaQueue/propertyMediaQueue";

export const MEDIA_KIND_PHOTO = "image";
export const MEDIA_KIND_PLAN = "plan";

export {
  createQueueItem,
  QUEUE_PHASE,
  revokeQueueItemPreview,
  canRetryUpload,
  normalizeUploadError,
  requireUploadFile,
  resetItemForUploadRetry,
  itemsByKind,
};

export function splitServerImages(property) {
  const images = (property && property.images) || [];
  return {
    photos: images.filter((img) => img.kind === MEDIA_KIND_PHOTO),
    plans: images.filter((img) => img.kind === MEDIA_KIND_PLAN),
  };
}

/** @deprecated use createQueueItem */
export function createPendingItem(file, localId, kind = MEDIA_KIND_PHOTO) {
  return createQueueItem(file, localId, kind);
}

export function pendingFiles(items) {
  return items
    .filter(isItemUploadReady)
    .map((it) => requireUploadFile(it));
}

export function hasUploadablePending(items) {
  return items.some(
    (it) => isItemUploadReady(it) || it.phase === QUEUE_PHASE.upload_failed,
  );
}

export function hasBlockingPrepare(items) {
  return items.some(
    (it) =>
      it.phase === QUEUE_PHASE.queued
      || it.phase === QUEUE_PHASE.checking
      || it.phase === QUEUE_PHASE.server_preview,
  );
}

export function hasInflightUploads(items) {
  return items.some((it) => it.phase === QUEUE_PHASE.uploading);
}

export function hasPendingMediaBlockers(items) {
  return (
    hasBlockingPrepare(items)
    || items.some((it) => it.phase === QUEUE_PHASE.prepare_failed)
    || items.some((it) => it.phase === QUEUE_PHASE.upload_failed)
  );
}

export function hasBlockingMediaAfterDrain(items) {
  return (
    hasPendingMediaBlockers(items)
    || hasInflightUploads(items)
    || items.some(isItemUploadReady)
  );
}

export const MAIN_ASSIGNMENT_STATUS = {
  patch_failed: "patch_failed",
  refresh_failed: "refresh_failed",
};

export function createMainAssignmentIntent(
  propertyId,
  serverImageId,
  status,
  error = null,
) {
  return {
    propertyId,
    serverImageId,
    status,
    error: error || null,
  };
}

export function hasPendingMainAssignmentBlocker(assignment) {
  return Boolean(
    assignment
    && (assignment.status === MAIN_ASSIGNMENT_STATUS.patch_failed
      || assignment.status === MAIN_ASSIGNMENT_STATUS.refresh_failed),
  );
}

export function markUploading(items, localIds) {
  const idSet = new Set(localIds);
  return items.map((it) =>
    idSet.has(it.localId)
      ? { ...it, phase: QUEUE_PHASE.uploading, uploadError: null }
      : it,
  );
}

/** Pure filter — caller revokes previews outside the state updater. */
export function applyUploadSuccess(items, localIds) {
  const idSet = new Set(localIds);
  return items.filter((it) => !idSet.has(it.localId));
}

export function takeUploadSuccessItems(items, localIds) {
  const idSet = new Set(localIds);
  return items.filter((it) => idSet.has(it.localId));
}

export function applyUploadFailures(items, failures) {
  const byId = new Map(failures.map((f) => [f.localId, f.error]));
  return items.map((it) => {
    if (!byId.has(it.localId)) return it;
    return {
      ...it,
      phase: QUEUE_PHASE.upload_failed,
      uploadError: byId.get(it.localId),
    };
  });
}

export function revokePendingPreviews(items, registry = null) {
  items.forEach((it) => revokeQueueItemPreview(it, registry));
}

export function resolvePendingMainAfterRemoval(
  remainingItems,
  removedLocalId,
  currentPendingMainLocalId,
  hasServerMain,
) {
  if (currentPendingMainLocalId !== removedLocalId) {
    return currentPendingMainLocalId;
  }
  if (hasServerMain) return null;
  const next = remainingItems.find((it) => isItemUploadReady(it));
  return next ? next.localId : null;
}

export function serverMainId(serverPhotos) {
  const main = (serverPhotos || []).find((img) => img.is_main);
  return main ? main.id : null;
}

export function uploadReadyItems(items) {
  return items.filter(isItemUploadReady);
}

export function uploadReadyItemsInOrder(items) {
  return items.filter(isItemUploadReady);
}

export class MediaWizardRetentionError extends Error {
  constructor(message) {
    super(message);
    this.name = "MediaWizardRetentionError";
    this.isMediaRetention = true;
  }
}

export function isMediaWizardRetentionError(err) {
  return Boolean(err?.isMediaRetention || err?.name === "MediaWizardRetentionError");
}

/** Block final save/publish before any draft/network mutation when media is not ready. */
export function assertCreateMediaPreflight(items, mainAssignment = null) {
  if (hasBlockingPrepare(items)) {
    throw new MediaWizardRetentionError(
      "Зачекайте завершення підготовки файлів.",
    );
  }
  if (
    items.some((it) => it.phase === QUEUE_PHASE.prepare_failed)
    || items.some((it) => it.phase === QUEUE_PHASE.upload_failed)
  ) {
    throw new MediaWizardRetentionError(
      "Виправте помилки медіа перед збереженням.",
    );
  }
  if (hasPendingMainAssignmentBlocker(mainAssignment)) {
    throw new MediaWizardRetentionError(
      mainAssignment.status === MAIN_ASSIGNMENT_STATUS.refresh_failed
        ? "Головне фото призначено, але список медіа не оновлено. Спробуйте ще раз."
        : "Не вдалося призначити головне фото. Спробуйте ще раз.",
    );
  }
}
